import React, { useState } from 'react';
import i18n from '../../../i18n';

const PlaceholderSelector = ({ placeholders, onSelect, onDragStart }) => {
    const [currentPath, setCurrentPath] = useState([]);
    const [currentItems, setCurrentItems] = useState(placeholders);

    const navigateToItem = (item, index) => {
        if (item.children) {
            setCurrentPath([...currentPath, { item, index }]);
            setCurrentItems(item.children);
        } else {
            // This is a leaf node, select it
            const fullPath = [...currentPath, { item, index }];
            const pathString = buildPathString(fullPath);
            onSelect(pathString);
        }
    };

    const handleListIteration = (item, index) => {
        // For list items, show only the loop template and nested variables
        const fullPath = [...currentPath, { item, index }];
        const basePath = buildPathString(fullPath);
        const loopLevel = currentPath.filter(p => p.item.list).length + 1;
        const loopVar = loopLevel === 1 ? 'loop' : `loop${loopLevel}`;
        
        // Create the loop variable name using the base path
        const varLoopName = `${basePath.replace(/\./g, '_')}_loop`;
        
        // Create options: empty loop template + nested variables
        const iterationOptions = [
            { 
                key: `{% for ${varLoopName} in ${basePath} %}\n\n\n{% endfor %}`, 
                label: `${item.label} Loop (Empty Template)`,
                isLoopTemplate: true
            }
        ];
        
        // Add nested variables that can be dragged into the loop
        if (item.children) {
            item.children.forEach(child => {
                if (child.children) {
                    // This is a nested object, add it as a folder that can be navigated
                    iterationOptions.push({
                        key: `${varLoopName}.${child.key}`,
                        label: child.label,
                        isLoopVariable: false, // This is a folder, not a draggable variable
                        children: child.children.map(grandChild => ({
                            key: `${varLoopName}.${child.key}.${grandChild.key}`,
                            label: grandChild.label,
                            isLoopVariable: true,
                            loopVar: varLoopName
                        }))
                    });
                } else {
                    // This is a simple property
                    iterationOptions.push({
                        key: `${varLoopName}.${child.key}`,
                        label: child.label,
                        isLoopVariable: true,
                        loopVar: varLoopName
                    });
                }
            });
        }
        
        // Create a temporary item to show loop options
        const iterationItem = {
            key: `${basePath}`,
            label: `${item.label} (Loop Options)`,
            children: iterationOptions.map(opt => ({
                key: opt.key,
                label: opt.label,
                isLoopTemplate: opt.isLoopTemplate,
                isLoopVariable: opt.isLoopVariable,
                loopVar: opt.loopVar,
                children: opt.children // Preserve children for nested objects
            }))
        };
        
        setCurrentPath([...currentPath, { item: iterationItem, index }]);
        setCurrentItems(iterationItem.children);
    };

    const navigateBack = () => {
        if (currentPath.length > 0) {
            const newPath = currentPath.slice(0, -1);
            setCurrentPath(newPath);
            if (newPath.length === 0) {
                setCurrentItems(placeholders);
            } else {
                setCurrentItems(newPath[newPath.length - 1].item.children);
            }
        }
    };

    const buildPathString = (path) => {
        return path.map(p => p.item.key).join('.');
    };

    const getBreadcrumb = () => {
        if (currentPath.length === 0) return null;
        
        const breadcrumb = currentPath.map((p, index) => (
            <span key={index}>
                <span 
                    className="text-primary cursor-pointer"
                    onClick={() => {
                        const newPath = currentPath.slice(0, index + 1);
                        setCurrentPath(newPath);
                        if (index === currentPath.length - 1) {
                            setCurrentItems(p.item.children);
                        } else {
                            setCurrentItems(newPath[index].item.children);
                        }
                    }}
                >
                    {p.item.label}
                </span>
                {index < currentPath.length - 1 && <span className="mx-1">/</span>}
            </span>
        ));
        
        return (
            <div className="breadcrumb-nav">
                <small className="text-muted">
                    <span 
                        className="text-primary cursor-pointer"
                        onClick={() => {
                            setCurrentPath([]);
                            setCurrentItems(placeholders);
                        }}
                    >
                        {i18n.t('Root')}
                    </span>
                    {currentPath.length > 0 && <span className="mx-1">/</span>}
                    {breadcrumb}
                </small>
            </div>
        );
    };

    const renderItem = (item, index) => {
        const hasChildren = item.children && item.children.length > 0;
        const isList = item.list;
        const isLoopTemplate = item.isLoopTemplate;
        const isLoopVariable = item.isLoopVariable;
        
        const handleClick = () => {
            if (isList) {
                handleListIteration(item, index);
            } else if (hasChildren) {
                navigateToItem(item, index);
            } else if (isLoopTemplate || isLoopVariable) {
                // For loop templates and variables, call onSelect directly
                onSelect(item.key);
            } else {
                navigateToItem(item, index);
            }
        };
        
        // Determine badge color and icon based on item type
        let badgeClass = 'bg-secondary';
        let icon = '📄';
        
        if (isLoopTemplate) {
            badgeClass = 'bg-primary';
            icon = '🔄';
        } else if (isLoopVariable) {
            badgeClass = 'bg-secondary';
            icon = '🔗';
        } else if (isList) {
            badgeClass = 'bg-info';
            icon = '📋';
        } else if (hasChildren) {
            badgeClass = 'bg-secondary';
            icon = '📁';
        }
        
        // Debug log to see what's happening
        console.log('Rendering item:', item.label, 'hasChildren:', hasChildren, 'isLoopVariable:', isLoopVariable, 'children:', item.children);
        
        // Render all items as badges
        return (
            <span
                key={index}
                className={`badge me-1 mb-1 cursor-pointer ${badgeClass}`}
                onClick={handleClick}
                draggable={!hasChildren && !isList}
                onDragStart={(e) => {
                    if (!hasChildren && !isList) {
                        // For loop templates and variables, use the item.key directly (it already has the correct format)
                        // For other items, build the path string
                        const pathString = (isLoopTemplate || isLoopVariable) ? item.key : buildPathString([...currentPath, { item, index }]);
                        onDragStart(e, pathString);
                    }
                }}
                style={{ 
                    cursor: hasChildren || isList ? 'pointer' : 'grab'
                }}
                title={`${hasChildren || isList ? 'Click to navigate' : 'Click or drag to insert'} ${item.key}`}
            >
                {icon} {item.label}
            </span>
        );
    };

    return (
        <div className="placeholder-selector">
            <div className="d-flex align-items-center mb-2">
                {getBreadcrumb()}
                {currentPath.length > 0 && (
                    <button 
                        className="btn btn-sm ms-2 p-1 d-inline"
                        onClick={navigateBack}
                    >
                        ← {i18n.t('Back')}
                    </button>
                )}
            </div>
            <div className="placeholder-list d-flex flex-wrap">
                {currentItems.map((item, index) => renderItem(item, index))}
            </div>
            
        </div>
    );
};

export default PlaceholderSelector;
